"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import {
  TicketNotification,
  NotificationType,
} from "@/types/ticketNotifications";
import { useAuth } from "@/context/providers/AuthProvider";
import { logger } from "@/utils/logger";

interface UseNotificationsOptions {
  enablePolling?: boolean;
  autoMarkAsRead?: boolean;
  loadOnMount?: boolean;
  refreshOnFocus?: boolean;
}

interface UseNotificationsReturn {
  notifications: TicketNotification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  loadingMore: boolean;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  createNotification: (
    ticketId: string,
    type: NotificationType,
    note?: string
  ) => Promise<void>;
  refreshNotifications: () => Promise<void>;
  loadMoreNotifications: () => Promise<void>;
}

const PAGE_SIZE = 20;

async function readApiPayload(response: Response) {
  const payload = await response.json().catch(() => null);
  if (!response.ok || !payload?.success) {
    throw new Error(payload?.error || "Notification request failed");
  }
  return payload.data;
}

export function useNotifications(
  options: UseNotificationsOptions = {}
): UseNotificationsReturn {
  const {
    enablePolling = true,
    autoMarkAsRead = false,
    loadOnMount = true,
    refreshOnFocus = enablePolling,
  } = options;
  const { user } = useAuth();

  const [notifications, setNotifications] = useState<TicketNotification[]>([]);
  const [loading, setLoading] = useState(loadOnMount);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const isInitialLoadRef = useRef(true);
  const inFlightInitialLoadRef = useRef<Promise<void> | null>(null);

  const fetchNotifications = useCallback(async (nextOffset: number) => {
    const response = await fetch(
      `/api/notifications?limit=${PAGE_SIZE}&offset=${nextOffset}`,
      {
        credentials: "include",
        cache: "no-store",
      }
    );
    return readApiPayload(response) as Promise<{
      notifications: TicketNotification[];
      hasMore: boolean;
      total?: number;
    }>;
  }, []);

  const loadInitialNotifications = useCallback(async () => {
    if (!user?.email) {
      setNotifications([]);
      setLoading(false);
      setError(null);
      setHasMore(false);
      setOffset(0);
      isInitialLoadRef.current = true;
      return;
    }

    if (inFlightInitialLoadRef.current) {
      return inFlightInitialLoadRef.current;
    }

    const loadPromise = (async () => {
      try {
        if (isInitialLoadRef.current) {
          setLoading(true);
        }
        setError(null);
        const result = await fetchNotifications(0);
        setNotifications(result.notifications);
        setHasMore(result.hasMore);
        setOffset(0);

        if (autoMarkAsRead) {
          const unreadIds = result.notifications
            .filter(n => !n.read)
            .map(n => n.id);
          await Promise.all(unreadIds.map(id => markNotificationRead(id)));
          setNotifications(prev => prev.map(n => ({ ...n, read: true })));
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to load notifications";
        setError(errorMessage);
      } finally {
        setLoading(false);
        isInitialLoadRef.current = false;
      }
    })();

    inFlightInitialLoadRef.current = loadPromise;
    try {
      await loadPromise;
    } finally {
      if (inFlightInitialLoadRef.current === loadPromise) {
        inFlightInitialLoadRef.current = null;
      }
    }
  }, [autoMarkAsRead, fetchNotifications, user?.email]);

  useEffect(() => {
    if (!loadOnMount) {
      setNotifications([]);
      setLoading(false);
      setError(null);
      setHasMore(false);
      setOffset(0);
      isInitialLoadRef.current = true;
      inFlightInitialLoadRef.current = null;
      return;
    }

    loadInitialNotifications();
  }, [loadInitialNotifications, loadOnMount]);

  const refreshNotifications = useCallback(async () => {
    await loadInitialNotifications();
  }, [loadInitialNotifications]);

  useEffect(() => {
    if (!enablePolling || !user?.email) {
      return;
    }

    const intervalId = window.setInterval(() => {
      refreshNotifications().catch(err => {
        logger.warn(
          "Notification polling failed",
          { component: "useNotifications" },
          err instanceof Error ? err : new Error(String(err))
        );
      });
    }, 60_000);

    return () => window.clearInterval(intervalId);
  }, [enablePolling, refreshNotifications, user?.email]);

  useEffect(() => {
    if (!refreshOnFocus) {
      return;
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && user?.email) {
        refreshNotifications();
      }
    };

    const handleFocus = () => {
      if (user?.email) {
        refreshNotifications();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, [refreshNotifications, refreshOnFocus, user?.email]);

  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      setNotifications(prev =>
        prev.map(n => (n.id === notificationId ? { ...n, read: true } : n))
      );
      await markNotificationRead(notificationId);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to mark notification as read";
      setError(errorMessage);
      setNotifications(prev =>
        prev.map(n => (n.id === notificationId ? { ...n, read: false } : n))
      );
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    const unreadNotifications = notifications.filter(n => !n.read);

    try {
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      const response = await fetch("/api/notifications", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ all: true }),
      });
      await readApiPayload(response);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to mark all as read";
      setError(errorMessage);
      setNotifications(prev =>
        prev.map(n => {
          const wasUnread = unreadNotifications.some(
            unread => unread.id === n.id
          );
          return wasUnread ? { ...n, read: false } : n;
        })
      );
    }
  }, [notifications]);

  const createNotification = useCallback(
    async (ticketId: string, type: NotificationType, note?: string) => {
      try {
        const response = await fetch("/api/notifications", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ ticketId, type, note }),
        });
        const data = await readApiPayload(response);
        if (data?.notification) {
          setNotifications(prev => [data.notification, ...prev].slice(0, 100));
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to create notification";
        setError(errorMessage);
      }
    },
    []
  );

  const loadMoreNotifications = useCallback(async () => {
    if (!user?.email || loadingMore || !hasMore) return;

    try {
      setLoadingMore(true);
      setError(null);
      const nextOffset = offset + PAGE_SIZE;
      const result = await fetchNotifications(nextOffset);
      setNotifications(prev => [...prev, ...result.notifications]);
      setHasMore(result.hasMore);
      setOffset(nextOffset);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to load more notifications";
      setError(errorMessage);
      logger.error(
        "Error loading more notifications",
        { component: "useNotifications", offset: offset + PAGE_SIZE },
        err instanceof Error ? err : new Error(String(err))
      );
    } finally {
      setLoadingMore(false);
    }
  }, [fetchNotifications, hasMore, loadingMore, offset, user?.email]);

  const unreadCount = notifications.filter(n => !n.read).length;

  return {
    notifications,
    unreadCount,
    loading,
    error,
    hasMore,
    loadingMore,
    markAsRead,
    markAllAsRead,
    createNotification,
    refreshNotifications,
    loadMoreNotifications,
  };
}

async function markNotificationRead(notificationId: string) {
  const response = await fetch("/api/notifications", {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ notificationId }),
  });
  await readApiPayload(response);
}
